import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { Description, ErrorMessage, Field, Label } from '@oysterjs/ui/fieldset';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { AutoResizeTextarea } from '@oysterjs/ui/textarea';

import type {
  ExcelTemplateFormState,
  ExcelTemplateTabFormEntry
} from '~/components/library/lib/excel-template-tabs';

export interface ExcelTemplateTabFieldsProps {
  form: ExcelTemplateFormState;
  updateForm: (
    path: string[],
    updater: (prev: ExcelTemplateFormState) => ExcelTemplateFormState
  ) => void;
  getValidationError: (path: string[]) => string | undefined;
}

/**
 * Collapsible list of tab cards. The workbook defines which tabs exist, so
 * unlike Word template sections these cannot be added, removed, or reordered
 * here — each card only edits the guidance and flags Cara uses when filling
 * that worksheet.
 */
export const ExcelTemplateTabFields = (props: ExcelTemplateTabFieldsProps) => {
  const tabs = props.form.tabs;

  const patchTab = (i: number, field: string, patch: Partial<ExcelTemplateTabFormEntry>) => {
    props.updateForm(['tabs', String(i), field], (prev) => ({
      ...prev,
      tabs: prev.tabs.map((tab, idx) => (idx === i ? { ...tab, ...patch } : tab))
    }));
  };

  if (tabs.length === 0) {
    return (
      <Field>
        <Label>Tabs</Label>
        <Description>
          Cara discovers the workbook&apos;s tabs while processing it. They appear here once
          processing finishes.
        </Description>
      </Field>
    );
  }

  return (
    <Field>
      <Label>Tabs</Label>
      <Description>
        One card per worksheet, in workbook order. Tabs come from the uploaded file — replace the
        workbook to change them.
      </Description>
      <div className="space-y-4" data-slot="control">
        {tabs.map((tab, i) => (
          <ExcelTabCard
            key={tab.id}
            tab={tab}
            defaultOpen={props.getValidationError(['tabs', String(i)]) !== undefined}
            onChange={(field, patch) => patchTab(i, field, patch)}
            error={props.getValidationError(['tabs', String(i)])}
          />
        ))}
      </div>
    </Field>
  );
};

interface ExcelTabCardProps {
  tab: ExcelTemplateTabFormEntry;
  defaultOpen: boolean;
  onChange: (field: string, patch: Partial<ExcelTemplateTabFormEntry>) => void;
  error?: string;
}

const ExcelTabCard = ({ tab, defaultOpen, onChange, error }: ExcelTabCardProps) => {
  const summaryParts: string[] = [];
  if (tab.static) summaryParts.push('Static');
  else if (!tab.required) summaryParts.push('Optional');
  else summaryParts.push('Required');
  if (tab.setupGuidance?.trim()) summaryParts.push('Has setup guidance');
  const summary = summaryParts.join(' · ');

  return (
    <Headless.Disclosure as="div" defaultOpen={defaultOpen}>
      {({ open }) => (
        <div className="border border-neutral-200 rounded-lg bg-white dark:bg-neutral-800 dark:border-neutral-700 shadow-sm">
          <Headless.DisclosureButton className="flex w-full items-center justify-between p-4 text-left hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors">
            <div className="flex flex-1 items-center gap-3 min-w-0">
              <ChevronDownIcon
                className={clsx(
                  'h-5 w-5 text-neutral-400 dark:text-neutral-500 transition-transform duration-200 shrink-0',
                  open && 'rotate-180'
                )}
              />
              <div className="flex-1 min-w-0">
                <span className="block min-w-0 truncate text-sm font-medium text-neutral-900 dark:text-white">
                  <span className="inline-flex items-center gap-2">
                    <span>{tab.name}</span>
                    {error && (
                      <span className="inline-block size-2 rounded-full bg-red-500 align-middle" />
                    )}
                  </span>
                </span>
                <div className="block min-w-0 truncate text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                  {summary}
                </div>
              </div>
            </div>
          </Headless.DisclosureButton>

          <Headless.DisclosurePanel
            unmount={false}
            className="px-4 pb-4 space-y-4 border-t border-neutral-200 dark:border-neutral-700 pt-4"
          >
            {error && <ErrorMessage>{error}</ErrorMessage>}

            <Field>
              <Label>Setup guidance (optional)</Label>
              <Description>
                Context for Cara when it analyzes this worksheet. Use it to clarify which cells are
                inputs, how rows repeat, or what must stay untouched. Applied the next time the
                template is processed.
              </Description>
              <AutoResizeTextarea
                value={tab.setupGuidance ?? ''}
                rows={3}
                resizable={false}
                onChange={(e) =>
                  onChange('setupGuidance', { setupGuidance: e.currentTarget.value })
                }
                placeholder="Example: One row per scheduled vehicle, starting at row 8. Column F is a formula — leave it alone."
              />
            </Field>

            <SwitchField>
              <Label>Static</Label>
              <Description>
                This worksheet is reference or boilerplate content. Cara copies it through unchanged
                instead of filling it.
              </Description>
              <Switch
                color="sky"
                checked={tab.static}
                onChange={(checked) =>
                  // A static tab is never filled, so it cannot also be required
                  // to be filled; the server rejects that combination.
                  onChange('static', { static: checked, required: checked ? false : tab.required })
                }
              />
            </SwitchField>

            <SwitchField>
              <Label>Required</Label>
              <Description>
                This worksheet must be filled in every workbook. Turn off to let Cara drop it when
                it is not relevant to the request.
              </Description>
              <Switch
                color="sky"
                disabled={tab.static}
                checked={tab.required}
                onChange={(checked) => onChange('required', { required: checked })}
              />
            </SwitchField>

            {tab.fillGuidance ? (
              <Field>
                <Label>What Cara extracted</Label>
                <Description>
                  Read-only. Generated when the workbook was processed, from the worksheet plus your
                  setup guidance.
                </Description>
                <p
                  data-slot="control"
                  className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300"
                >
                  {tab.fillGuidance}
                </p>
              </Field>
            ) : null}
          </Headless.DisclosurePanel>
        </div>
      )}
    </Headless.Disclosure>
  );
};
